import { useState } from "react";
import {
  Caso,
  Holding,
  Cliente,
  Propiedad,
  CasoPropiedad,
  Poder,
  Estudio,
  Solicitud,
  Gasto,
  Usuario,
  EntradaBitacora,
  Documento,
  EventoEstado,
  Reclamo,
  Cobro,
  Tesoreria,
  AnalisisEstudio,
} from "../../types";
import { Modal } from "../common/Modal";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { Badge, getEstadoCasoBadgeVariant } from "../common/Badge";
import { CasoForm } from "./CasoForm";
import { CasoDetail } from "./CasoDetail";

interface CasosViewProps {
  casos: Caso[];
  holdings: Holding[];
  clientes: Cliente[];
  propiedades: Propiedad[];
  casoPropiedades: CasoPropiedad[];
  poderes: Poder[];
  estudios: Estudio[];
  solicitudes: Solicitud[];
  gastos: Gasto[];
  usuarios: Usuario[];
  bitacora: EntradaBitacora[];
  documentos: Documento[];
  usuarioActual: Usuario;
  onCasoCreate: (caso: Omit<Caso, "id" | "fechaCreacion" | "fechaActualizacion">) => void;
  onCasoUpdate: (caso: Caso) => void;
  onCasoDelete: (id: number) => void;
  onCasoPropiedadCreate: (data: Omit<CasoPropiedad, "id">) => void;
  onCasoPropiedadDelete: (id: number) => void;
  onEstudioCreate: (data: Omit<Estudio, "id">) => void;
  onEstudioUpdate: (estudio: Estudio) => void;
  onSolicitudCreate: (data: Omit<Solicitud, "id">) => void;
  onSolicitudUpdate: (solicitud: Solicitud) => void;
  onGastoCreate: (data: Omit<Gasto, "id">) => void;
  onGastoUpdate: (gasto: Gasto) => void;
  onGastoDelete: (id: number) => void;
  onBitacoraCreate: (entrada: Omit<EntradaBitacora, "id" | "fechaCreacion">) => void;
  onDocumentoCreate: (documento: Omit<Documento, "id" | "fechaSubida">) => void;
  onDocumentoDelete: (id: number) => void;
  eventosEstado: EventoEstado[];
  reclamos: Reclamo[];
  cobros: Cobro[];
  tesoreriaItems: Tesoreria[];
  onCambioEstadoSolicitud: (caso: Caso, nuevoEstado: Caso["estado"], comentario: string, documentoId?: number) => void;
  analisisEstudios: AnalisisEstudio[];
}

export function CasosView({
  casos,
  holdings,
  clientes,
  propiedades,
  casoPropiedades,
  poderes,
  estudios,
  solicitudes,
  gastos,
  usuarios,
  bitacora,
  documentos,
  usuarioActual,
  onCasoCreate,
  onCasoUpdate,
  onCasoDelete,
  onCasoPropiedadCreate,
  onCasoPropiedadDelete,
  onEstudioCreate,
  onEstudioUpdate,
  onSolicitudCreate,
  onSolicitudUpdate,
  onGastoCreate,
  onGastoUpdate,
  onGastoDelete,
  onBitacoraCreate,
  onDocumentoCreate,
  onDocumentoDelete,
  eventosEstado,
  reclamos,
  cobros,
  tesoreriaItems,
  onCambioEstadoSolicitud,
  analisisEstudios,
}: CasosViewProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEstado, setFilterEstado] = useState<string>("activos");
  const [filterHolding, setFilterHolding] = useState<number | "all">("all");
  const [selectedCaso, setSelectedCaso] = useState<Caso | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCaso, setEditingCaso] = useState<Caso | null>(null);
  const [casoToDelete, setCasoToDelete] = useState<Caso | null>(null);

  const getHoldingNombre = (holdingId: number) => {
    const holding = holdings.find((h) => h.id === holdingId);
    return holding ? holding.nombre : "-";
  };

  const filteredCasos = casos.filter((c) => {
    const holdingNombre = getHoldingNombre(c.holdingId);
    const matchesSearch =
      c.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      holdingNombre.toLowerCase().includes(searchTerm.toLowerCase());

    // Handle special filter values
    let matchesEstado = false;
    if (filterEstado === "all") {
      matchesEstado = true;
    } else if (filterEstado === "activos") {
      matchesEstado = c.estado !== "desistido";
    } else if (filterEstado === "desistidos") {
      matchesEstado = c.estado === "desistido";
    } else {
      matchesEstado = c.estado === filterEstado;
    }

    const matchesHolding = filterHolding === "all" || c.holdingId === filterHolding;

    return matchesSearch && matchesEstado && matchesHolding;
  });

  const formatMoney = (value?: number) =>
    value ? `$${value.toLocaleString("es-CL")}` : "-";

  const handleEdit = (caso: Caso) => {
    setEditingCaso(caso);
    setIsFormOpen(true);
  };

  const handleFormSubmit = (
    data: Omit<Caso, "id" | "fechaCreacion" | "fechaActualizacion">
  ) => {
    if (editingCaso) {
      onCasoUpdate({
        ...editingCaso,
        ...data,
        fechaActualizacion: new Date().toISOString().split("T")[0],
      });
    } else {
      onCasoCreate(data);
    }
    setIsFormOpen(false);
    setEditingCaso(null);
  };

  return (
    <div className="flex h-full">
      {/* Lista de casos */}
      <div className="w-2/5 border-r border-slate-200 flex flex-col">
        <div className="p-6 border-b border-slate-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-slate-800">Solicitudes</h2>
            <button
              onClick={() => {
                setEditingCaso(null);
                setIsFormOpen(true);
              }}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              + Nueva Solicitud
            </button>
          </div>

          <div className="space-y-3">
            <input
              type="text"
              placeholder="Buscar por titulo o cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <div className="flex gap-2">
              <select
                value={filterHolding}
                onChange={(e) =>
                  setFilterHolding(
                    e.target.value === "all" ? "all" : Number(e.target.value)
                  )
                }
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todos los Clientes</option>
                {holdings.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </select>

              <select
                value={filterEstado}
                onChange={(e) => setFilterEstado(e.target.value)}
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="activos">--- Activos ---</option>
                <option value="all">Todos</option>
                <option value="rol_listo">ROL Listo</option>
                <option value="en_preparacion">En Preparacion</option>
                <option value="confirmacion_presentacion">Confirmacion/Presentacion</option>
                <option value="solicitud_administrativa">Solicitud Admin.</option>
                <option value="resolucion">En Resolucion</option>
                <option value="ganado">Ganado</option>
                <option value="parcialmente_ganado">Parcialmente Ganado</option>
                <option value="perdido">Perdido</option>
                <option value="desistidos">--- Desistidos ---</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-2">
            {filteredCasos.map((caso) => (
              <div
                key={caso.id}
                onClick={() => setSelectedCaso(caso)}
                className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                  selectedCaso?.id === caso.id
                    ? "border-blue-500 bg-blue-50"
                    : "border-slate-200 hover:border-slate-300 bg-white"
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-slate-800 truncate">
                        {caso.titulo}
                      </h3>
                      <Badge variant={getEstadoCasoBadgeVariant(caso.estado)}>
                        {caso.estado.replace("_", " ")}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      {getHoldingNombre(caso.holdingId)}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span>{caso.tipoPresentacion}</span>
                      <span>{caso.lugarPresentacion}</span>
                      {caso.rebajaEsperada && (
                        <span>Rebaja: {formatMoney(caso.rebajaEsperada)}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(caso);
                      }}
                      className="p-1.5 text-slate-400 hover:text-blue-600 transition-colors"
                      title="Editar"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCasoToDelete(caso);
                      }}
                      className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"
                      title="Eliminar"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {filteredCasos.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                {searchTerm || filterEstado !== "activos" || filterHolding !== "all"
                  ? "No se encontraron casos"
                  : "No hay casos registrados"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detalle del caso */}
      <div className="w-3/5 overflow-y-auto">
        {selectedCaso ? (
          <CasoDetail
            caso={selectedCaso}
            holding={holdings.find((h) => h.id === selectedCaso.holdingId)!}
            clientes={clientes}
            propiedades={propiedades}
            casoPropiedades={casoPropiedades.filter((cp) => cp.casoId === selectedCaso.id)}
            poderes={poderes}
            estudios={estudios.filter((e) => e.casoId === selectedCaso.id)}
            solicitudes={solicitudes.filter((s) => s.casoId === selectedCaso.id)}
            gastos={gastos.filter((g) => g.casoId === selectedCaso.id)}
            usuarios={usuarios}
            bitacora={bitacora.filter((b) => b.casoId === selectedCaso.id)}
            documentos={documentos.filter((d) => d.casoId === selectedCaso.id)}
            usuarioActual={usuarioActual}
            onCasoUpdate={onCasoUpdate}
            onCasoPropiedadCreate={onCasoPropiedadCreate}
            onCasoPropiedadDelete={onCasoPropiedadDelete}
            onEstudioCreate={onEstudioCreate}
            onEstudioUpdate={onEstudioUpdate}
            onSolicitudCreate={onSolicitudCreate}
            onSolicitudUpdate={onSolicitudUpdate}
            onGastoCreate={onGastoCreate}
            onGastoUpdate={onGastoUpdate}
            onGastoDelete={onGastoDelete}
            onBitacoraCreate={onBitacoraCreate}
            onDocumentoCreate={onDocumentoCreate}
            onDocumentoDelete={onDocumentoDelete}
            eventosEstado={eventosEstado.filter((e) => e.entidadTipo === "solicitud" && e.entidadId === selectedCaso.id)}
            reclamos={reclamos.filter((r) => r.solicitudId === selectedCaso.id)}
            cobros={cobros.filter((c) => c.solicitudId === selectedCaso.id)}
            tesoreriaItems={tesoreriaItems.filter((t) => t.solicitudId === selectedCaso.id)}
            onCambioEstadoSolicitud={onCambioEstadoSolicitud}
            analisisEstudios={analisisEstudios}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-slate-400">
            Selecciona un caso para ver detalles
          </div>
        )}
      </div>

      {/* Modal de formulario */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingCaso(null);
        }}
        title={editingCaso ? "Editar Solicitud" : "Nueva Solicitud"}
        size="lg"
      >
        <CasoForm
          caso={editingCaso}
          holdings={holdings}
          propiedades={propiedades}
          clientes={clientes}
          usuarios={usuarios}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setIsFormOpen(false);
            setEditingCaso(null);
          }}
        />
      </Modal>

      {/* Dialogo de confirmacion */}
      <ConfirmDialog
        isOpen={casoToDelete !== null}
        onClose={() => setCasoToDelete(null)}
        onConfirm={() => {
          if (casoToDelete) {
            onCasoDelete(casoToDelete.id);
            if (selectedCaso?.id === casoToDelete.id) {
              setSelectedCaso(null);
            }
          }
        }}
        title="Eliminar Caso"
        message={`Esta seguro que desea eliminar el caso "${casoToDelete?.titulo}"? Se eliminaran tambien la bitacora y documentos asociados.`}
        confirmText="Eliminar"
      />
    </div>
  );
}
